import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, Loading, EmptyState, Gauge, fmtHours, fmtNum } from "../components/common";
import { Button } from "../components/ui/button";
import ProcessMap from "../components/ProcessMap";
import Markdown from "../components/Markdown";
import client, { downloadFile } from "../lib/api";
import { toast } from "sonner";
import {
  Gauge as GaugeIcon, ZoomIn, ZoomOut, Maximize2, FileDown, Sparkles, Loader2, Hash, Activity,
} from "lucide-react";

function Inner() {
  const { datasetId, currentDataset } = useApp();
  const { data, loading } = useFetch(datasetId ? `/datasets/${datasetId}/assessment` : null, { skip: !datasetId });
  const [zoom, setZoom] = useState(1);
  const [metric, setMetric] = useState("frequency");
  const [narr, setNarr] = useState("");
  const [narrLoad, setNarrLoad] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!datasetId) return;
    setNarr(""); setNarrLoad(true);
    client.post("/ai/insight", { dataset_id: datasetId })
      .then(({ data }) => setNarr(data.insight)).catch(() => {}).finally(() => setNarrLoad(false));
  }, [datasetId]);

  if (!datasetId) return <EmptyState icon={GaugeIcon} title="No dataset selected" />;
  if (loading) return <Loading label="Compiling assessment…" />;
  if (!data) return null;

  const { kpis, variants, performance, conformance, dfg } = data;
  const exportPdf = async () => {
    setExporting(true);
    try { await downloadFile(`/reports/${datasetId}/pdf?report_type=Process Assessment`, `${currentDataset?.name || "assessment"}.pdf`); toast.success("Report downloaded"); }
    catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const KPIS = [
    ["Cases", fmtNum(kpis.total_cases)], ["Events", fmtNum(kpis.total_events)],
    ["Avg Cycle", fmtHours(kpis.avg_duration_hours)], ["Variants", kpis.variant_count],
    ["Rework", performance.rework_cases], ["Automation", `${kpis.automation_rate}%`],
  ];

  return (
    <div className="pm-fade-up flex flex-col" style={{ height: "calc(100vh - 150px)" }}>
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div>
          <div className="text-xs font-semibold tracking-wider uppercase text-cyan-700/80">Visual Assessment</div>
          <h1 className="text-2xl font-extrabold text-slate-900">{currentDataset?.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="glass rounded-xl p-1 flex gap-1">
            {[["frequency", Hash], ["performance", Activity]].map(([v, Icon]) => (
              <button key={v} onClick={() => setMetric(v)} data-testid={`assess-metric-${v}`}
                className={`p-2 rounded-lg pm-transition ${metric === v ? "pm-gradient text-white" : "text-slate-500"}`}><Icon className="w-4 h-4" /></button>
            ))}
          </div>
          <div className="glass rounded-xl p-1 flex items-center gap-1">
            <button onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))} data-testid="zoom-out" className="p-2 rounded-lg text-slate-500 hover:text-slate-800"><ZoomOut className="w-4 h-4" /></button>
            <span className="text-xs font-semibold text-slate-600 w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))} data-testid="zoom-in" className="p-2 rounded-lg text-slate-500 hover:text-slate-800"><ZoomIn className="w-4 h-4" /></button>
            <button onClick={() => setZoom(1)} data-testid="zoom-reset" className="p-2 rounded-lg text-slate-500 hover:text-slate-800"><Maximize2 className="w-4 h-4" /></button>
          </div>
          <Button onClick={exportPdf} disabled={exporting} data-testid="export-pdf" className="rounded-xl pm-gradient text-white border-0 h-10">{exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1.5" />Export PDF</>}</Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <GlassCard className="col-span-12 lg:col-span-8 p-3 flex flex-col min-h-0">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-2 shrink-0">
            {KPIS.map(([l, v]) => (
              <div key={l} className="rounded-xl bg-white/60 border border-white/60 px-2 py-1.5 text-center">
                <div className="text-base font-bold text-slate-900 font-display leading-tight">{v}</div>
                <div className="text-[9px] uppercase tracking-wide text-slate-400">{l}</div>
              </div>
            ))}
          </div>
          <div className="flex-1 overflow-auto rounded-xl">
            <div style={{ transform: `scale(${zoom})`, transformOrigin: "top left", transition: "transform 0.2s", width: `${100 / zoom}%` }}>
              <ProcessMap data={dfg} metric={metric} />
            </div>
          </div>
        </GlassCard>

        <div className="col-span-12 lg:col-span-4 flex flex-col gap-4 min-h-0">
          <GlassCard className="p-4 flex items-center gap-4 shrink-0">
            {conformance && <Gauge value={conformance.conformance_rate} label="Conformance" size={110} />}
            <div className="text-sm space-y-1.5">
              <div className="flex justify-between gap-6"><span className="text-slate-500">Top variant</span><span className="font-semibold text-slate-800">{variants[0]?.percentage}%</span></div>
              <div className="flex justify-between gap-6"><span className="text-slate-500">Bottleneck</span><span className="font-semibold text-amber-600">{fmtHours(performance.bottlenecks[0]?.avg_wait_hours)}</span></div>
              <div className="flex justify-between gap-6"><span className="text-slate-500">Fitness</span><span className="font-semibold text-slate-800">{conformance?.overall_fitness}%</span></div>
            </div>
          </GlassCard>
          <GlassCard className="p-4 flex-1 flex flex-col min-h-0">
            <div className="flex items-center gap-2 mb-2 shrink-0"><div className="w-7 h-7 rounded-lg pm-gradient flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div><h3 className="font-bold text-slate-900 text-sm">AI narration</h3></div>
            <div className="overflow-y-auto flex-1 pr-1" data-testid="assessment-narration">
              {narrLoad ? <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Narrating…</div> : <Markdown className="text-xs">{narr}</Markdown>}
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}

export default function Assessment() { return <NeedTenant><Inner /></NeedTenant>; }
